import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Utensils, BookOpen, Dumbbell, Car, Bus, Bike, Clock, Zap, Users, MapPin, Building, Flag, Plus, X, Footprints, HeartPulse, Trophy, Phone, Store, ShoppingBag, Coffee, Scissors, Landmark, Mail, ExternalLink, CalendarPlus, Star, UserPlus, Check, ArrowDownWideNarrow } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import OriginPicker from "@/components/OriginPicker";
import { withDistance, estimateTo, fmtMiles, gapFit } from "@/lib/distance";
import { downloadIcs } from "@/lib/ics";
import { formatTime, useTimeFormat } from "@/lib/time";
import { enablePush } from "@/lib/push";
import { Bell, BellRing } from "lucide-react";
import { DormsView, ApartmentsView, GreekView } from "@/pages/HousingViews";
import OffCampusHome from "@/components/OffCampusHome";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { k: "dining", label: "Dining", icon: Utensils },
  { k: "study", label: "Study", icon: BookOpen },
  { k: "rec", label: "Rec", icon: Dumbbell },
  { k: "athletics", label: "Athletics", icon: Trophy },
  { k: "health", label: "Health", icon: HeartPulse },
  { k: "housing", label: "Housing", icon: Building },
  { k: "parking", label: "Parking", icon: Car },
  { k: "buses", label: "Buses", icon: Bus },
  { k: "services", label: "Services", icon: Store },
];
const HEALTH_ICON = { health_center: HeartPulse, pharmacy: Plus, counseling: Users };
const SERVICE_ICON = { bookstore: BookOpen, convenience: ShoppingBag, coffee: Coffee, barber: Scissors, bank: Landmark, mail: Mail };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const DIETS = ["Vegan", "Vegetarian", "Halal", "Gluten-free"];
const levelColor = { Low: "text-[#166534] bg-[#16A34A]/10", Moderate: "text-[#92400e] bg-[#F59E0B]/15", Busy: "text-[#991b1b] bg-destructive/10" };
const gapTone = { good: "text-[#166534] bg-[#16A34A]/10", mod: "text-[#92400e] bg-[#F59E0B]/15", busy: "text-[#991b1b] bg-destructive/10" };


// Sort By control for Dining / Study / Rec directories.
function SortSelect({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <ArrowDownWideNarrow size={15} strokeWidth={1.6} className="text-muted-foreground" />
      <select
        data-testid="places-sort-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border border-border rounded-md px-3 py-2 text-sm font-semibold outline-none focus:ring-2 focus:ring-brand cursor-pointer"
      >
        <option value="distance">Sort: Distance (nearest)</option>
        <option value="alpha">Sort: Alphabetical (A–Z)</option>
      </select>
    </div>
  );
}

// "I'm Interested" + "Invite Friends" for a campus event (game or rec class).
function RsvpControls({ evt, interested, friendNames, onToggle, onInvite }) {
  const on = interested.has(evt.id);
  return (
    <div className="mt-3 pt-3 border-t border-border" data-testid={`rsvp-${evt.id}`}>
      <div className="flex items-center gap-2">
        <button
          data-testid={`rsvp-interest-${evt.id}`}
          onClick={() => onToggle(evt)}
          className={`flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2 transition-colors ${
            on ? "bg-brand text-white" : "bg-white border border-border hover:border-brand text-foreground"
          }`}
        >
          <Star size={13} strokeWidth={1.8} className={on ? "fill-white" : ""} />
          {on ? "Interested" : "I'm Interested"}
        </button>
        <button
          data-testid={`rsvp-invite-${evt.id}`}
          onClick={() => onInvite(evt)}
          className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2 border border-border hover:border-brand transition-colors"
        >
          <UserPlus size={13} strokeWidth={1.8} /> Invite friends
        </button>
      </div>
      {friendNames?.length > 0 && (
        <div className="mt-2 text-[11px] text-muted-foreground" data-testid={`rsvp-friends-${evt.id}`}>
          {friendNames.slice(0, 3).join(", ")}{friendNames.length > 3 ? ` +${friendNames.length - 3}` : ""} interested
        </div>
      )}
    </div>
  );
}

// Walk time + distance + "fits my gap" hint, shown on each place card.
function DistanceRow({ est, testid }) {
  if (!est) return null;
  const g = gapFit(est.walkMin);
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={testid}>
      <span className="flex items-center gap-1.5 text-sm font-semibold text-brand">
        <Footprints size={14} strokeWidth={1.8} /> {est.walkMin} min walk
        <span className="text-muted-foreground font-normal mono text-xs">· {fmtMiles(est.miles)}</span>
      </span>
      {g && <span className={`mono text-[11px] font-bold px-2 py-0.5 rounded ${gapTone[g.tone]}`}>{g.label}</span>}
    </div>
  );
}

function fmtWhen(iso, fmt = "12") {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: fmt === "24" ? "2-digit" : "numeric", minute: "2-digit", hour12: fmt !== "24" });
}

function GameChip({ e, onRemind, reminded }) {
  const [fmt] = useTimeFormat();
  const live = e.state === "in";
  const done = e.completed;
  const hasScore = e.home?.score != null && e.away?.score != null;
  const canRemind = typeof onRemind === "function" && isFuture(e) && !live;
  return (
    <div className="flex items-center gap-3" data-testid={`game-${e.id}`}>
      {e.opponent_logo
        ? <img src={e.opponent_logo} alt="" className="w-9 h-9 object-contain shrink-0" />
        : <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0"><Trophy size={16} strokeWidth={1.6} className="text-brand" /></div>}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="mono text-[11px] font-bold uppercase tracking-widest text-brand">{e.sport}</span>
          {live && <span className="mono text-[11px] font-bold uppercase tracking-widest text-white bg-destructive rounded px-1.5 py-0.5 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live</span>}
          {done && !live && <span className="mono text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Final</span>}
        </div>
        <div className="font-semibold text-sm leading-tight truncate">{e.vs_prefix} {e.opponent}</div>
        <div className="text-xs text-muted-foreground mono truncate">
          {live ? (e.detail || "In progress")
            : done ? (hasScore ? `${e.away.abbr} ${e.away.score} — ${e.home.abbr} ${e.home.score}` : (e.detail || "Final"))
            : fmtWhen(e.date, fmt)}
        </div>
      </div>
      {canRemind && (
        <button
          data-testid={`game-remind-${e.id}`}
          onClick={(ev) => { ev.stopPropagation(); onRemind(e); }}
          title={reminded ? "Reminder set — tap to remove" : "Remind me before this game"}
          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center border transition-colors ${reminded ? "bg-brand text-white border-brand" : "bg-white text-brand border-border hover:border-brand"}`}
        >
          {reminded ? <BellRing size={15} strokeWidth={1.8} /> : <Bell size={15} strokeWidth={1.8} />}
        </button>
      )}
    </div>
  );
}

function RotatingGames({ items, testid, onRemind, remindedSet, renderFooter }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!items || items.length <= 1) return;
    const id = setInterval(() => setI((p) => (p + 1) % items.length), 5000);
    return () => clearInterval(id);
  }, [items]);
  if (!items || items.length === 0) return null;
  const e = items[i % items.length];
  return (
    <div className="relative" data-testid={testid}>
      <AnimatePresence mode="wait">
        <motion.div key={e.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }}>
          <GameChip e={e} onRemind={onRemind} reminded={remindedSet?.has(e.id)} />
        </motion.div>
      </AnimatePresence>
      {items.length > 1 && (
        <div className="flex gap-1 mt-2">
          {items.map((_, idx) => <span key={idx} className={`h-1 rounded-full transition-all ${idx === (i % items.length) ? "w-4 bg-brand" : "w-1.5 bg-border"}`} />)}
        </div>
      )}
      {renderFooter && renderFooter(e)}
    </div>
  );
}

function isFuture(e) {
  if (e.state === "in") return true;        // live now
  if (e.completed || e.state === "post") return false;
  if (e.date) return new Date(e.date) >= new Date(Date.now() - 3 * 3600 * 1000);
  return true;
}

function venueMatches(evVenue, name) {
  const a = (evVenue || "").toLowerCase(), b = (name || "").toLowerCase();
  if (!a || !b) return false;
  if (a.includes(b) || b.includes(a)) return true;
  const wa = new Set(a.split(/\s+/).filter((w) => w.length > 4));
  return b.split(/\s+/).some((w) => w.length > 4 && wa.has(w));
}

export default function Places() {
  const TAB_KEYS = ["dining", "study", "rec", "athletics", "health", "housing", "parking", "buses", "services"];
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TAB_KEYS.includes(t) ? t : "dining";
  });
  const [data, setData] = useState(null);
  const [diet, setDiet] = useState(null);
  const [recDay, setRecDay] = useState("Mon");
  const [stops, setStops] = useState([]);
  const [busData, setBusData] = useState({});
  const [report, setReport] = useState(null); // {kind, housing_id, name, address, note}
  const [center, setCenter] = useState(null);
  const [userLoc, setUserLoc] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [schoolId, setSchoolId] = useState(null);
  const [rt, setRt] = useState(null);
  const [ath, setAth] = useState(null);
  const [reminders, setReminders] = useState(new Set());
  const [remindTarget, setRemindTarget] = useState(null);
  const [fmt, setFmt] = useTimeFormat();
  const [housingView, setHousingView] = useState("residence");
  const [greekType, setGreekType] = useState("all");
  const [sortBy, setSortBy] = useState("distance");
  // Campus events RSVP ("I'm Interested" + "Invite Friends")
  const [interested, setInterested] = useState(new Set());
  const [eventFriends, setEventFriends] = useState({});
  const [eventInvites, setEventInvites] = useState([]);
  const [friendsList, setFriendsList] = useState([]);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [inviteSel, setInviteSel] = useState([]);

  const { user, checkAuth } = useAuth();
  const homeApplied = useRef(false);
  // Off-campus students: default the start point to their saved Home (skip the demo campus).
  useEffect(() => {
    if (!homeApplied.current && user?.home_origin && !origin && schoolId && schoolId !== "demo") {
      homeApplied.current = true;
      const h = user.home_origin;
      setOrigin({ id: "home", name: h.label, lat: h.lat, lng: h.lng });
    }
  }, [user, origin, schoolId]);

  const effectiveLoc = origin ? [origin.lat, origin.lng] : (userLoc || center);
  const navigate = useNavigate();

  // Stash an event draft and jump to Today, where ScheduleBuilder opens it pre-filled.
  const addToEvents = (draft) => {
    localStorage.setItem("routerev_event_draft", JSON.stringify(draft));
    toast.success("Opening your schedule to set the time…");
    navigate("/");
  };
  const gameToDraft = (e, v) => {
    const pad = (n) => String(n).padStart(2, "0");
    const d = e.date ? new Date(e.date) : null;
    const draft = {
      name: `${e.sport}: ${e.vs_prefix || "vs"} ${e.opponent}`.trim(),
      category: "other", location: v.name, lat: v.lat, lng: v.lng,
      start_time: d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : "18:00",
      end_time: "", days: [], event_date: "",
    };
    if (d) {
      const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
      draft.event_date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      if (["Mon", "Tue", "Wed", "Thu", "Fri"].includes(wd)) draft.days = [wd];
    }
    return draft;
  };

  const openReport = (kind, h) => setReport({
    kind, housing_id: h?.id || "", name: h?.name || "", address: h?.address || "", note: "",
  });
  const submitReport = async () => {
    if (report.kind === "missing" && !report.name.trim()) { toast.error("Add the complex name."); return; }
    try {
      const res = await api.post("/housing/reports", report);
      if (res.data?.added) {
        toast.success(`Added "${res.data.added.name}" — visible now, pending admin verification.`);
        api.get("/places").then((r) => setData(r.data));
      } else {
        toast.success("Thanks! Sent to campus admins for review.");
      }
      setReport(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send report."); }
  };

  useEffect(() => {
    api.get("/places").then((r) => setData(r.data));
    api.get("/campus").then((r) => {
      setStops(r.data.bus_stops);
      setCenter(r.data.school.center);
      setSchoolId(r.data.school.id);
      // Demo campus: default the start to an on-campus dorm so distances stay realistic
      // even if the presenter's real GPS is granted.
      if (r.data.school.id === "demo") {
        setOrigin((o) => o || { id: "demo_start", name: "Nebula Hall", lat: 39.7716, lng: -86.1555 });
      }
    });
    navigator.geolocation?.getCurrentPosition(
      (pos) => setUserLoc([pos.coords.latitude, pos.coords.longitude]),
      () => {},
      { timeout: 5000 }
    );
  }, []);

  useEffect(() => {
    if (tab !== "buses" || stops.length === 0) return;
    const fetchAll = () => {
      stops.forEach((s) => api.get(`/bus/eta?stop_id=${s.id}`).then((r) => setBusData((p) => ({ ...p, [s.id]: r.data.arrivals }))));
    };
    fetchAll();
    const id = setInterval(fetchAll, 20000);
    return () => clearInterval(id);
  }, [tab, stops]);

  useEffect(() => {
    if (tab !== "buses") return;
    let alive = true;
    const poll = () => api.get("/transit/realtime").then((r) => { if (alive) setRt(r.data); }).catch(() => {});
    poll();
    const t = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [tab]);

  useEffect(() => {
    if (tab !== "athletics") return;
    let alive = true;
    const load = () => api.get("/athletics/events")
      .then((r) => { if (alive) setAth(r.data); })
      .catch(() => { if (alive) setAth((p) => p || { events: [], by_venue: {} }); });
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [tab]);

  useEffect(() => {
    if (tab !== "athletics") return;
    api.get("/athletics/reminders").then((r) => setReminders(new Set(r.data.game_ids))).catch(() => {});
  }, [tab]);

  const loadEvents = () => {
    api.get("/events/interested").then((r) => {
      setInterested(new Set(r.data.event_ids));
      setEventFriends(r.data.friends_by_event || {});
    }).catch(() => {});
    api.get("/events/invites").then((r) => setEventInvites(r.data.invites || [])).catch(() => {});
  };
  useEffect(() => {
    loadEvents();
    api.get("/friends").then((r) => setFriendsList(r.data || [])).catch(() => {});
  }, []);

  const toggleInterest = async (evt) => {
    const on = interested.has(evt.id);
    setInterested((prev) => { const n = new Set(prev); on ? n.delete(evt.id) : n.add(evt.id); return n; });
    try {
      if (on) await api.delete(`/events/rsvp/${encodeURIComponent(evt.id)}`);
      else {
        await api.post("/events/rsvp", { event_type: evt.type, event_id: evt.id, title: evt.title, when: evt.when, venue: evt.venue });
        toast.success("You're interested — invite friends to join!");
      }
    } catch { toast.error("Could not update"); loadEvents(); }
  };
  const openInvite = (evt) => { setInviteSel([]); setInviteTarget(evt); };
  const sendInvite = async () => {
    if (inviteSel.length === 0) { toast.error("Pick at least one friend"); return; }
    try {
      await api.post("/events/invite", {
        event_type: inviteTarget.type, event_id: inviteTarget.id, title: inviteTarget.title,
        when: inviteTarget.when, venue: inviteTarget.venue, friend_ids: inviteSel,
      });
      toast.success(`Invite sent to ${inviteSel.length} friend${inviteSel.length > 1 ? "s" : ""}`);
      setInviteTarget(null);
    } catch { toast.error("Could not send invites"); }
  };
  const respondInvite = async (inv, accept) => {
    try {
      await api.post(`/events/invites/${inv.id}/respond`, { accept });
      setEventInvites((list) => list.filter((x) => x.id !== inv.id));
      if (accept) { setInterested((prev) => new Set(prev).add(inv.event_id)); toast.success("Added to your interested events"); }
    } catch { toast.error("Could not respond"); }
  };

  const dining = withDistance((data?.dining || []).filter((d) => !diet || d.dietary.includes(diet)), effectiveLoc);
  const studySpaces = withDistance(data?.study_spaces, effectiveLoc);
  const parking = withDistance(data?.parking, effectiveLoc);
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
  // "Sort By" for Dining / Study / Rec — Distance (nearest) is the default; A–Z alternative.
  const sortList = (list) => (sortBy === "alpha" ? [...list].sort(byName) : list);
  const housing = withDistance(data?.housing, effectiveLoc).sort(byName);
  const dorms = withDistance(data?.dorms, effectiveLoc).sort(byName);
  const greek = withDistance(data?.greek, effectiveLoc).sort(byName);
  const greekFiltered = greek.filter((g) => greekType === "all" || g.type === greekType);
  const venues = withDistance(data?.athletics, effectiveLoc);
  const healthList = withDistance(data?.health, effectiveLoc);
  // On-campus map: the "Next up" banner surfaces HOME games only (exclude away & neutral-site).
  const upcomingGames = (ath?.events || []).filter((e) => e.is_home_game && !e.neutral && isFuture(e));
  const recFacilities = withDistance(data?.rec_facilities, effectiveLoc);
  const services = withDistance(data?.services, effectiveLoc);
  const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const todayName = DAY_SHORT[new Date().getDay()];
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
  const dayClasses = (data?.rec_classes || []).filter((r) => r.day === recDay).sort((a, b) => toMin(a.start) - toMin(b.start));
  const recIsLive = (r) => todayName === recDay && toMin(r.start) <= nowMin && nowMin < toMin(r.end);
  const nowNext = todayName === recDay ? (dayClasses.find(recIsLive) || dayClasses.find((r) => toMin(r.start) >= nowMin) || null) : null;

  const handleRemind = (e) => {
    if (reminders.has(e.id)) {
      api.delete(`/athletics/reminders/${e.id}`).catch(() => {});
      setReminders((prev) => { const n = new Set(prev); n.delete(e.id); return n; });
    } else {
      setRemindTarget(e);
    }
  };
  const saveReminder = async (e, lead) => {
    try {
      await api.post("/athletics/reminders", { game_id: e.id, sport: e.sport, opponent: e.opponent, venue: e.venue, start: e.date, lead_min: lead });
      setReminders((prev) => new Set(prev).add(e.id));
      enablePush({}).catch(() => {});
    } catch (err) { /* ignore */ }
    setRemindTarget(null);
  };

  return (
    <AppShell>
      <div className="px-4 lg:px-6 py-5">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Campus resources</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-3">Places &amp; Services</h1>

        {/* Distance origin: live GPS by default, or pick a saved place / address (same as Today) */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="places-origin-row">
          <OriginPicker center={center} value={origin} onChange={setOrigin} />
          <span className="mono text-[11px] tracking-widest uppercase text-muted-foreground">
            Distances from {origin ? origin.label : "your location"}
          </span>
        </div>

        {eventInvites.length > 0 && (
          <div className="mb-5 space-y-2" data-testid="event-invites">
            {eventInvites.map((inv) => (
              <div key={inv.id} data-testid={`event-invite-${inv.id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-xl border border-brand/30 bg-brand/[0.05] p-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {inv.from_name?.[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">
                      <b>{inv.from_name}</b> invited you to {inv.title}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate mono">{inv.when}{inv.venue ? ` · ${inv.venue}` : ""}</div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`event-invite-accept-${inv.id}`} onClick={() => respondInvite(inv, true)}
                    className="flex items-center gap-1 text-xs font-semibold bg-brand text-white rounded-lg px-3 py-2 hover:opacity-90 transition-opacity">
                    <Check size={13} strokeWidth={2} /> I'm in
                  </button>
                  <button data-testid={`event-invite-dismiss-${inv.id}`} onClick={() => respondInvite(inv, false)}
                    className="text-xs font-semibold rounded-lg px-3 py-2 border border-border hover:bg-secondary transition-colors">
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-1.5 overflow-x-auto hide-scrollbar mb-5" data-testid="places-tabs">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`places-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold shrink-0 transition-colors ${
                tab === t.k ? "bg-brand text-white" : "bg-white border border-border hover:border-brand"
              }`}
            >
              <t.icon size={15} strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>

        {/* DINING */}
        {tab === "dining" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setDiet(null)} data-testid="diet-all"
                  className={`text-xs font-bold px-3 py-1.5 rounded-md ${!diet ? "bg-brand text-white" : "bg-white border border-border"}`}>All</button>
                {DIETS.map((d) => (
                  <button key={d} data-testid={`diet-${d}`} onClick={() => setDiet(d)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-md ${diet === d ? "bg-brand text-white" : "bg-white border border-border"}`}>{d}</button>
                ))}
              </div>
              <SortSelect value={sortBy} onChange={setSortBy} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortList(dining).map((d) => (
                <motion.div key={d.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-border rounded-xl p-5" data-testid={`dining-${d.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg font-semibold leading-tight">{d.name}</h3>
                    <span className={`mono text-[11px] font-bold px-2 py-1 rounded ${levelColor[d.level]}`}>{d.level}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 mono">
                    <Clock size={12} strokeWidth={1.5} /> {d.hours}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm mt-3">
                    <Users size={14} strokeWidth={1.5} className="text-brand" />
                    <span className="font-semibold">~{d.wait_min} min wait</span>
                    <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground ml-1">simulated</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {d.dietary.map((t) => (
                      <span key={t} className="text-[11px] font-semibold bg-secondary rounded px-2 py-0.5">{t}</span>
                    ))}
                  </div>
                  {d.eateries?.length > 0 && (
                    <div className="mt-3" data-testid={`dining-eateries-${d.id}`}>
                      <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">Eateries inside</div>
                      <div className="flex flex-wrap gap-1.5">
                        {d.eateries.map((e) => (
                          <span key={e} className="text-[11px] font-semibold bg-brand/[0.07] text-brand rounded px-2 py-0.5">{e}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <DistanceRow est={d._est} testid={`dining-dist-${d.id}`} />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* STUDY */}
        {tab === "study" && (
          <div>
            <div className="flex justify-end mb-4">
              <SortSelect value={sortBy} onChange={setSortBy} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sortList(studySpaces).map((s) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-border rounded-xl p-5" data-testid={`study-${s.id}`}>
                <h3 className="font-display text-lg font-semibold leading-tight">{s.name}</h3>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 mono">
                  <Clock size={12} strokeWidth={1.5} /> {s.hours}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className={`text-[11px] font-bold rounded px-2 py-0.5 uppercase mono ${s.zone === "quiet" ? "bg-blue-100 text-blue-800" : s.zone === "group" ? "bg-[#F59E0B]/15 text-[#92400e]" : "bg-secondary"}`}>{s.zone}</span>
                  <span className="text-[11px] font-semibold bg-secondary rounded px-2 py-0.5 flex items-center gap-1">
                    <Zap size={11} strokeWidth={2} /> {s.outlets} outlets
                  </span>
                  {s.rooms > 0 && <span className="text-[11px] font-semibold bg-secondary rounded px-2 py-0.5">{s.rooms} study rooms</span>}
                </div>
                <DistanceRow est={s._est} testid={`study-dist-${s.id}`} />
              </motion.div>
            ))}
            </div>
          </div>
        )}

        {/* REC */}
        {tab === "rec" && (
          <div data-testid="rec-panel">
            <div className="flex justify-end mb-4">
              <SortSelect value={sortBy} onChange={setSortBy} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
              {sortList(recFacilities).map((f) => (
                <motion.div key={f.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-border rounded-xl p-5" data-testid={`rec-facility-${f.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-md bg-brand/10 text-brand flex items-center justify-center">
                      <Dumbbell size={16} strokeWidth={1.5} />
                    </div>
                    <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">Recreation</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold leading-tight">{f.name}</h3>
                  {f.tag && <div className="text-sm text-muted-foreground mt-1">{f.tag}</div>}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2 mono">
                    <Clock size={12} strokeWidth={1.5} /> {f.hours}
                  </div>
                  <DistanceRow est={f._est} testid={`rec-facility-dist-${f.id}`} />
                </motion.div>
              ))}
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground">Group fitness · weekly schedule</div>
              <button data-testid="rec-time-format-toggle" onClick={() => setFmt(fmt === "12" ? "24" : "12")}
                title="Toggle 12h / 24h time"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-border hover:border-brand transition-colors mono text-xs font-bold">
                <Clock size={13} strokeWidth={1.6} /> {fmt === "12" ? "12h" : "24h"}
              </button>
            </div>

            <div className="flex gap-1.5 mb-4 overflow-x-auto hide-scrollbar">
              {DAYS.map((d) => (
                <button key={d} data-testid={`rec-day-${d}`} onClick={() => setRecDay(d)}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold mono shrink-0 flex items-center gap-1 ${recDay === d ? "bg-brand text-white" : "bg-white border border-border"}`}>
                  {d}{d === todayName && <span className={`w-1.5 h-1.5 rounded-full ${recDay === d ? "bg-white" : "bg-brand"}`} />}
                </button>
              ))}
            </div>

            {nowNext && (
              <div className="mb-3 rounded-xl border border-brand/30 bg-brand/[0.04] p-3 flex items-center gap-3" data-testid="rec-nownext">
                <div className={`mono text-[11px] font-bold uppercase tracking-widest rounded px-1.5 py-0.5 ${recIsLive(nowNext) ? "text-white bg-destructive" : "text-white bg-brand"}`}>
                  {recIsLive(nowNext) ? "Now" : "Next"}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{nowNext.name}</div>
                  <div className="text-xs text-muted-foreground mono truncate">{formatTime(nowNext.start, fmt)}–{formatTime(nowNext.end, fmt)} · {nowNext.location}</div>
                </div>
              </div>
            )}

            <div className="bg-white border border-border rounded-xl divide-y divide-border">
              {dayClasses.map((r) => {
                const live = recIsLive(r);
                return (
                  <div key={r.id} className={`flex items-center gap-3 p-4 ${live ? "bg-brand/[0.04]" : ""}`} data-testid={`rec-${r.id}`}>
                    <div className="mono text-xs font-bold text-brand w-28 shrink-0">{formatTime(r.start, fmt)}–{formatTime(r.end, fmt)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold flex items-center gap-2">
                        {r.name}
                        {live && <span className="mono text-[11px] font-bold uppercase tracking-widest text-white bg-destructive rounded px-1.5 py-0.5">Now</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{r.location}</div>
                    </div>
                    {(() => {
                      const evt = { id: `rec:${r.day}:${r.name}:${r.start}`, type: "rec", title: r.name, when: `${r.day} ${formatTime(r.start, fmt)}`, venue: r.location };
                      const on = interested.has(evt.id);
                      return (
                        <div className="flex items-center gap-1 shrink-0">
                          <button data-testid={`rec-interest-${r.id}`} onClick={() => toggleInterest(evt)} title={on ? "You're interested" : "I'm interested"}
                            className={`p-2 rounded-md transition-colors ${on ? "bg-brand text-white" : "text-brand hover:bg-brand/10"}`}>
                            <Star size={15} strokeWidth={1.8} className={on ? "fill-white" : ""} />
                          </button>
                          <button data-testid={`rec-invite-${r.id}`} onClick={() => openInvite(evt)} title="Invite friends"
                            className="p-2 rounded-md text-muted-foreground hover:bg-secondary transition-colors">
                            <UserPlus size={15} strokeWidth={1.8} />
                          </button>
                        </div>
                      );
                    })()}
                    <button data-testid={`rec-add-${r.id}`} title="Add to my events"
                      onClick={() => addToEvents({ name: r.name, category: "other", location: r.location, lat: r.lat, lng: r.lng, days: [r.day], start_time: r.start, end_time: r.end, event_date: "" })}
                      className="p-2 rounded-md text-brand hover:bg-brand/10 transition-colors shrink-0">
                      <CalendarPlus size={15} strokeWidth={1.8} />
                    </button>
                    <span className="mono text-xs text-muted-foreground shrink-0">{r.spots} spots</span>
                  </div>
                );
              })}
              {dayClasses.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">No scheduled classes on {recDay} — open recreation available.</div>
              )}
            </div>
          </div>
        )}

        {/* HOUSING */}
        {tab === "housing" && (
          <div data-testid="housing-panel">
            {schoolId !== "demo" && (
              <OffCampusHome
                center={center}
                value={user?.home_origin}
                complexes={housing}
                onSaved={(home) => { homeApplied.current = true; setOrigin({ id: "home", name: home.label, lat: home.lat, lng: home.lng }); checkAuth?.(); }}
                onCleared={() => { homeApplied.current = true; setOrigin(null); checkAuth?.(); }}
              />
            )}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="housing-subtabs">
              {[
                { k: "residence", label: "Dorms", count: dorms.length },
                { k: "apartments", label: "Apartments", count: housing.length },
                { k: "greek", label: "Greek", count: greek.length },
              ].map((v) => (
                <button
                  key={v.k}
                  data-testid={`housing-subtab-${v.k}`}
                  onClick={() => setHousingView(v.k)}
                  className={`text-sm font-semibold rounded-full border px-4 py-1.5 transition-colors ${
                    housingView === v.k ? "bg-brand text-white border-brand" : "bg-white border-border text-muted-foreground hover:border-brand"
                  }`}
                >
                  {v.label}
                  <span className={`ml-1.5 text-[11px] ${housingView === v.k ? "text-white/80" : "text-muted-foreground/70"}`}>{v.count}</span>
                </button>
              ))}
            </div>

            {housingView === "residence" && <DormsView dorms={dorms} DistanceRow={DistanceRow} />}
            {housingView === "apartments" && (
              <ApartmentsView housing={housing} hasData={!!(data?.housing && data.housing.length)}
                openReport={openReport} DistanceRow={DistanceRow} />
            )}
            {housingView === "greek" && (
              <GreekView greek={greekFiltered} greekType={greekType} setGreekType={setGreekType} DistanceRow={DistanceRow} />
            )}
          </div>
        )}

        {/* PARKING */}
        {tab === "parking" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {parking.map((p) => {
              const Icon = p.type === "bike" ? Bike : p.type === "scooter" ? Bike : p.type === "garage" ? Car : Car;
              return (
                <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-border rounded-xl p-5" data-testid={`parking-${p.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-md bg-brand/10 text-brand flex items-center justify-center">
                      <Icon size={16} strokeWidth={1.5} />
                    </div>
                    <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{p.type}</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold leading-tight">{p.name}</h3>
                  <div className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
                    <MapPin size={13} strokeWidth={1.5} /> {p.near}
                  </div>
                  <span className="inline-block mt-3 text-[11px] font-semibold bg-secondary rounded px-2 py-0.5">{p.permit}</span>
                  <DistanceRow est={p._est} testid={`parking-dist-${p.id}`} />
                </motion.div>
              );
            })}
          </div>
        )}

        {/* BUSES */}
        {tab === "buses" && (
          <div>
            {rt?.live ? (
              <div data-testid="rt-status-live" className="flex items-center gap-2 mb-3 text-sm font-semibold text-[#166534] bg-[#16A34A]/10 rounded-lg px-3 py-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#16A34A] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#16A34A]" />
                </span>
                LIVE · {rt.vehicles.length} {rt.vehicles.length === 1 ? "bus" : "buses"} tracked
                <span className="mono text-[11px] text-muted-foreground font-normal">via {rt.source}</span>
              </div>
            ) : (
              <div data-testid="rt-status-scheduled" className="flex items-center gap-2 mb-3 text-sm font-medium text-[#92400e] bg-[#F59E0B]/12 rounded-lg px-3 py-2">
                <Clock size={14} strokeWidth={1.8} />
                Scheduled times — no live vehicle feed for this campus yet.
              </div>
            )}
            <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground mb-3">
              {rt?.live ? "Arrivals blend live positions + schedule" : "Arrivals estimated from the schedule (simulated)"}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {stops.map((s) => (
                <div key={s.id} className="bg-white border border-border rounded-xl p-5" data-testid={`busstop-${s.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-md bg-black text-white flex items-center justify-center mono text-xs font-bold">B</div>
                    <h3 className="font-semibold">{s.name}</h3>
                  </div>
                  <div className="space-y-2">
                    {(busData[s.id] || []).map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.color }} />
                        <span className="flex-1 truncate">{a.route}</span>
                        <span className="mono font-bold text-brand">{a.eta_min === 0 ? "Now" : `${a.eta_min}m`}</span>
                      </div>
                    ))}
                    {!busData[s.id] && <div className="mono text-xs text-muted-foreground animate-pulse">Loading…</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ATHLETICS */}
        {tab === "athletics" && (
          <div data-testid="athletics-panel">
            <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground mb-3">
              Gameday feed · live matchups, times &amp; scores via ESPN
            </div>
            {ath === null ? (
              <div className="mono text-xs text-muted-foreground animate-pulse mb-4" data-testid="athletics-loading">Loading matchups…</div>
            ) : upcomingGames.length > 0 ? (
              <div className="mb-5 rounded-xl border border-brand/30 bg-brand/[0.04] p-4" data-testid="athletics-ticker">
                <div className="mono text-[11px] tracking-widest uppercase text-brand mb-2">Next up</div>
                <RotatingGames items={upcomingGames.slice(0, 12)} testid="athletics-ticker-rot" onRemind={handleRemind} remindedSet={reminders} />
              </div>
            ) : (
              <div className="mb-5 rounded-xl border border-border bg-white p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="athletics-empty">
                <Trophy size={15} strokeWidth={1.6} className="text-brand" /> No home games coming up — check back for the next matchup on campus.
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {venues.map((v) => {
                const games = (ath?.events || []).filter((e) => e.is_home_game && isFuture(e) && venueMatches(e.venue, v.name)).slice(0, 6);
                return (
                  <motion.div key={v.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-border rounded-xl p-5" data-testid={`athletics-${v.id}`}>
                    {v.photo && (
                      <img src={v.photo} alt={v.name} loading="lazy" data-testid={`athletics-photo-${v.id}`}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        className="w-full h-28 object-cover rounded-lg mb-3" />
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-md bg-[#7c3aed]/10 text-[#7c3aed] flex items-center justify-center">
                        <Trophy size={16} strokeWidth={1.5} />
                      </div>
                      <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{v.tag}</span>
                    </div>
                    <h3 className="font-display text-lg font-semibold leading-tight">{v.name}</h3>
                    <DistanceRow est={v._est} testid={`athletics-dist-${v.id}`} />
                    <div className="mt-3 pt-3 border-t border-border">
                      {games.length > 0 ? (
                        <RotatingGames items={games} testid={`athletics-games-${v.id}`} onRemind={handleRemind} remindedSet={reminders}
                          renderFooter={(e0) => {
                            const evt = { id: e0.id, type: "game", title: `${e0.sport}: ${e0.vs_prefix || "vs"} ${e0.opponent}`, when: fmtWhen(e0.date, fmt), venue: v.name };
                            return (
                              <>
                                <button data-testid={`athletics-add-${v.id}`} onClick={() => addToEvents(gameToDraft(e0, v))}
                                  className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-[#7c3aed] border border-[#7c3aed]/30 rounded-lg py-2 hover:bg-[#7c3aed]/5 transition-colors">
                                  <CalendarPlus size={13} strokeWidth={1.8} /> Add this game to my events
                                </button>
                                <button data-testid={`athletics-ics-${v.id}`}
                                  onClick={() => {
                                    const start = e0.date ? new Date(e0.date) : new Date();
                                    const end = new Date(start.getTime() + 2.5 * 3600 * 1000);
                                    downloadIcs({ uid: `game-${e0.id}`, title: `${e0.sport}: ${e0.vs_prefix || "vs"} ${e0.opponent}`, start, end, location: v.name, alarmMin: 60 });
                                  }}
                                  className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-brand border border-border rounded-lg py-2 hover:bg-brand/5 transition-colors">
                                  <CalendarPlus size={13} strokeWidth={1.8} /> Add game to calendar (.ics)
                                </button>
                                <RsvpControls evt={evt} interested={interested} friendNames={eventFriends[evt.id]} onToggle={toggleInterest} onInvite={openInvite} />
                              </>
                            );
                          }}
                        />
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-brand mono" data-testid={`athletics-soon-${v.id}`}>
                          <Clock size={12} strokeWidth={1.8} /> Season coming soon
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* HEALTH */}
        {tab === "health" && (
          <div data-testid="health-panel">
            <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground mb-3">
              Campus health, pharmacy &amp; counseling
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {healthList.map((h) => {
                const Icon = HEALTH_ICON[h.type] || HeartPulse;
                return (
                  <motion.div key={h.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-border rounded-xl p-5" data-testid={`health-${h.id}`}>
                    {h.photo && (
                      <img src={h.photo} alt={h.name} loading="lazy" data-testid={`health-photo-${h.id}`}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        className="w-full h-28 object-cover rounded-lg mb-3" />
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-md bg-[#0891b2]/10 text-[#0891b2] flex items-center justify-center">
                        <Icon size={16} strokeWidth={1.5} />
                      </div>
                      <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{(h.type || "").replace("_", " ")}</span>
                    </div>
                    <h3 className="font-display text-lg font-semibold leading-tight">{h.name}</h3>
                    {h.tag && <div className="text-sm text-muted-foreground mt-1">{h.tag}</div>}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2 mono">
                      <Clock size={12} strokeWidth={1.5} /> {h.hours}
                    </div>
                    {h.phone && (
                      <a href={`tel:${h.phone}`} data-testid={`health-call-${h.id}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0891b2] hover:underline">
                        <Phone size={13} strokeWidth={1.8} /> {h.phone}
                      </a>
                    )}
                    {h.booking_url && (
                      <a href={h.booking_url} target="_blank" rel="noopener noreferrer" data-testid={`health-book-${h.id}`}
                        className="mt-3 flex items-center justify-center gap-1.5 text-sm font-semibold text-white bg-[#0891b2] rounded-lg py-2 hover:opacity-90 transition-opacity">
                        <ExternalLink size={13} strokeWidth={1.8} /> Book appointment
                      </a>
                    )}
                    <DistanceRow est={h._est} testid={`health-dist-${h.id}`} />
                    <button data-testid={`health-add-${h.id}`}
                      onClick={() => addToEvents({ name: h.name, category: "meeting", location: h.name, lat: h.lat, lng: h.lng, days: [], start_time: "12:00", end_time: "", event_date: "", booking_url: h.booking_url || "" })}
                      className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-[#0891b2] border border-[#0891b2]/30 rounded-lg py-2 hover:bg-[#0891b2]/5 transition-colors">
                      <CalendarPlus size={13} strokeWidth={1.8} /> Add to my events
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* SERVICES */}
        {tab === "services" && (
          <div data-testid="services-panel">
            <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground mb-3">
              Bookstores, shops &amp; campus services
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {services.map((s) => {
                const Icon = SERVICE_ICON[s.type] || Store;
                return (
                  <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-border rounded-xl p-5" data-testid={`service-${s.id}`}>
                    {s.photo && (
                      <img src={s.photo} alt={s.name} loading="lazy" data-testid={`service-photo-${s.id}`}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        className="w-full h-28 object-cover rounded-lg mb-3" />
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-md bg-[#ea580c]/10 text-[#ea580c] flex items-center justify-center">
                        <Icon size={16} strokeWidth={1.5} />
                      </div>
                      <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{(s.type || "").replace("_", " ")}</span>
                    </div>
                    <h3 className="font-display text-lg font-semibold leading-tight">{s.name}</h3>
                    {s.tag && <div className="text-sm text-muted-foreground mt-1">{s.tag}</div>}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2 mono">
                      <Clock size={12} strokeWidth={1.5} /> {s.hours}
                    </div>
                    {s.booking_url ? (
                      <a href={s.booking_url} target="_blank" rel="noopener noreferrer" data-testid={`service-book-${s.id}`}
                        className="mt-3 flex items-center justify-center gap-1.5 text-sm font-semibold text-white bg-[#ea580c] rounded-lg py-2 hover:opacity-90 transition-opacity">
                        <ExternalLink size={13} strokeWidth={1.8} /> Book appointment
                      </a>
                    ) : s.phone ? (
                      <a href={`tel:${s.phone}`} data-testid={`service-call-${s.id}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#ea580c] hover:underline">
                        <Phone size={13} strokeWidth={1.8} /> Call to book
                      </a>
                    ) : null}
                    <DistanceRow est={s._est} testid={`service-dist-${s.id}`} />
                    {s.booking_url && (
                      <button data-testid={`service-add-${s.id}`}
                        onClick={() => addToEvents({ name: s.name, category: "meeting", location: s.name, lat: s.lat, lng: s.lng, days: [], start_time: "12:00", end_time: "", event_date: "", booking_url: s.booking_url || "" })}
                        className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-[#ea580c] border border-[#ea580c]/30 rounded-lg py-2 hover:bg-[#ea580c]/5 transition-colors">
                        <CalendarPlus size={13} strokeWidth={1.8} /> Add to my events
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {report && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setReport(null)} data-testid="housing-report-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }} onClick={(e) => e.stopPropagation()}
              className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl border border-border overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-border">
                <div>
                  <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Housing feedback</div>
                  <h3 className="font-display text-lg font-semibold">{report.kind === "missing" ? "Add a missing complex" : "Report incorrect info"}</h3>
                </div>
                <button data-testid="housing-report-close" onClick={() => setReport(null)} className="p-2 rounded-md hover:bg-secondary transition-colors"><X size={18} strokeWidth={1.5} /></button>
              </div>
              <div className="p-5 space-y-3">
                <label className="block">
                  <span className="mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">Complex name</span>
                  <input data-testid="report-name" value={report.name} onChange={(e) => setReport({ ...report, name: e.target.value })}
                    readOnly={report.kind === "incorrect"} placeholder="e.g. The Landing at College Station"
                    className={`mt-1 w-full rounded-md border border-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand ${report.kind === "incorrect" ? "bg-secondary/50 text-muted-foreground" : "bg-background"}`} />
                </label>
                <label className="block">
                  <span className="mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">Correct address {report.kind === "incorrect" ? "(if wrong)" : ""}</span>
                  <input data-testid="report-address" value={report.address} onChange={(e) => setReport({ ...report, address: e.target.value })}
                    placeholder="123 Main St, City, TX"
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand" />
                </label>
                <label className="block">
                  <span className="mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">What's wrong / anything else?</span>
                  <textarea data-testid="report-note" value={report.note} onChange={(e) => setReport({ ...report, note: e.target.value })}
                    placeholder="Optional details" className="mt-1 w-full h-20 rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand resize-none" />
                </label>
                <button data-testid="submit-housing-report" onClick={submitReport}
                  className="w-full bg-brand text-white rounded-md py-3 text-sm font-semibold hover:opacity-90 transition-colors">
                  Send to campus admins
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {remindTarget && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setRemindTarget(null)}
            className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            data-testid="game-remind-modal"
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl"
            >
              <div className="flex items-center gap-2 mb-1">
                <Bell size={16} strokeWidth={1.8} className="text-brand" />
                <span className="mono text-[11px] tracking-widest uppercase text-brand">Gameday reminder</span>
              </div>
              <h3 className="font-display text-xl font-semibold leading-tight">
                {remindTarget.sport}: {remindTarget.vs_prefix} {remindTarget.opponent}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">{fmtWhen(remindTarget.date, fmt)}{remindTarget.venue ? ` · ${remindTarget.venue}` : ""}</p>
              <p className="text-xs text-muted-foreground mt-3 mb-2">Notify me before it starts:</p>
              <div className="grid grid-cols-3 gap-2">
                {[[30, "30 min"], [60, "1 hour"], [120, "2 hours"]].map(([mins, label]) => (
                  <button key={mins} data-testid={`remind-lead-${mins}`} onClick={() => saveReminder(remindTarget, mins)}
                    className="py-2.5 rounded-lg border border-border hover:border-brand hover:bg-brand/5 transition-colors text-sm font-semibold">
                    {label}
                  </button>
                ))}
              </div>
              <button onClick={() => setRemindTarget(null)} data-testid="remind-cancel"
                className="mt-3 w-full text-sm text-muted-foreground hover:text-foreground py-2">Cancel</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {inviteTarget && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setInviteTarget(null)}
            className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            data-testid="event-invite-modal"
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl"
            >
              <div className="flex items-center gap-2 mb-1">
                <UserPlus size={16} strokeWidth={1.8} className="text-brand" />
                <span className="mono text-[11px] tracking-widest uppercase text-brand">Invite friends</span>
              </div>
              <h3 className="font-display text-lg font-semibold leading-tight">{inviteTarget.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{inviteTarget.when}{inviteTarget.venue ? ` · ${inviteTarget.venue}` : ""}</p>
              {friendsList.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-4">Add friends first to invite them to events.</p>
              ) : (
                <div className="mt-4 max-h-60 overflow-y-auto space-y-1.5 pr-1" data-testid="invite-friend-list">
                  {friendsList.map((f) => (
                    <label key={f.user_id} data-testid={`invite-pick-${f.user_id}`}
                      className="flex items-center gap-2.5 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-secondary transition-colors">
                      <input type="checkbox" className="accent-brand w-4 h-4"
                        checked={inviteSel.includes(f.user_id)}
                        onChange={() => setInviteSel((s) => s.includes(f.user_id) ? s.filter((x) => x !== f.user_id) : [...s, f.user_id])} />
                      <div className="w-7 h-7 rounded-full bg-brand text-white flex items-center justify-center text-xs font-bold">{f.name?.[0]}</div>
                      <span className="text-sm font-semibold">{f.name}</span>
                    </label>
                  ))}
                </div>
              )}
              <button data-testid="invite-send-btn" disabled={friendsList.length === 0} onClick={sendInvite}
                className="mt-4 w-full bg-brand text-white rounded-lg py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40">
                Send invite{inviteSel.length > 1 ? "s" : ""}
              </button>
              <button onClick={() => setInviteTarget(null)} data-testid="invite-cancel"
                className="mt-2 w-full text-sm text-muted-foreground hover:text-foreground py-2">Cancel</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppShell>
  );
}
